<template>
    <div id="commande-list">

        <h1 class="visually-hidden">Liste des commandes - PharmaConnect</h1>

        <b-modal ref="my-modal" scrollable title="Détail commande" ok-only ok-title="Annuler" size="xl"
            cancel-variant="outline-secondary">

            <b-table :bordered="true" :small="true" :hover="true" :no-border-collapse="true" :items="commandesSelected"
                :fields="fields" />

        </b-modal>

        <b-overlay :show="show" spinner-variant="primary">
            <b-card>

                <ag-grid :withFilters="true" :filterOpenByDefault="true" :filterComponent="AgGridDateFilter" :agGrid="agGrid"
                    pageTitle="Liste des commandes" @refresh="loadData" @filterClicked="filterClicked" />

            </b-card>
        </b-overlay>


    </div>
</template>

<script>
import AgGrid from "@/views/components/ag-grid/AgGrid.vue"
import ButtonCellRenderer from "@/views/components/ag-grid/ButtonCellRenderer.vue"
import BadgeCellRenderer from "@/views/components/ag-grid/BadgeCellRenderer.vue"
import AgGridDateFilter from '@/views/customer/docs/AgGridDateFilterCurrMonth';
import { formatPrice } from "@/utils/formatting";

export default {

    metaInfo() {
        return {
            title: "Liste des commandes - PharmaConnect",
            meta: [
                {
                    vmid: "description",
                    name: "description",
                    content:
                        "Consultez la liste complète de vos commandes pharmaceutiques sur PharmaConnect. Accédez facilement aux détails de chaque commande, statut, montant et plus encore.",
                },
                {
                    name: "robots",
                    content: "index, follow",
                },
                {
                    property: "og:title",
                    content: "Liste des commandes - PharmaConnect",
                },
                {
                    property: "og:description",
                    content:
                        "Consultez la liste complète de vos commandes pharmaceutiques sur PharmaConnect. Accédez facilement aux détails de chaque commande, statut, montant et plus encore.",
                },
                {
                    property: "og:image",
                    content: "https://www.pharmaconnect.net/images/orders-list-thumbnail.jpg",
                },
                {
                    property: "og:url",
                    content: "https://www.pharmaconnect.net/docs/orders",
                },
                {
                    property: "og:type",
                    content: "website",
                },
                {
                    name: "twitter:card",
                    content: "summary_large_image",
                },
                {
                    name: "twitter:title",
                    content: "Liste des commandes - PharmaConnect",
                },
                {
                    name: "twitter:description",
                    content:
                        "Consultez la liste complète de vos commandes pharmaceutiques sur PharmaConnect. Accédez facilement aux détails de chaque commande, statut, montant et plus encore.",
                },
                {
                    name: "twitter:image",
                    content: "https://www.pharmaconnect.net/images/orders-list-thumbnail.jpg",
                },
            ],
            link: [
                {
                    rel: "canonical",
                    href: "https://www.pharmaconnect.net/docs/orders",
                },
            ],
            script: [
                // Structured Data using Schema.org
                {
                    type: "application/ld+json",
                    json: {
                        "@context": "https://schema.org",
                        "@type": "WebPage",
                        "name": "Liste des commandes - PharmaConnect",
                        "description": "Consultez la liste complète de vos commandes pharmaceutiques sur PharmaConnect. Accédez facilement aux détails de chaque commande, statut, montant et plus encore.",
                        "brand": {
                            "@type": "Brand",
                            "name": "PharmaConnect"
                        }
                    },
                },
            ],
        };
    },

    components: {
        AgGrid,
    },
    data() {
        return {
            AgGridDateFilter: AgGridDateFilter,
            startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 2).toISOString().split('T')[0],
            endDate: new Date().toISOString().split('T')[0],
            agGrid: {
                columnDefs: [
                    {
                        headerName: "N° BL",
                        field: "Last_Shipping_No",
                        // width: 180
                    },
                    {
                        headerName: "Date Commande",
                        field: "Posting_Date",
                        valueGetter: function (tr) {
                            return tr.data.Posting_Date.substring(0, 10);
                        },
                        //cellClass: 'right-customeCellRender',
                        //headerClass: 'right-customeHeaderRender',
                        // width: 200
                    },
                    // {
                    //     headerName: "N° Commande",
                    //     field: "No",
                    //     // width: 180,
                    // },
                    {
                        headerName: "Commandé Par",
                        field: "User_ID",
                        // width: 200
                    },
                    {
                        headerName: "Type",
                        field: "Gen_Prod_Posting_Group",
                        cellRendererFramework: 'badgeCellRenderer',
                        cellRendererParams: { componentName: "Orders", columnName: "Gen_Prod_Posting_Group" },
                        // width: 100
                    },
                    {
                        headerName: "Montant",
                        field: "Amount",
                        valueGetter: function (tr) {
                            return formatPrice(tr.data.Amount);
                        },
                        //cellClass: 'right-customeCellRender',
                        //headerClass: 'right-customeHeaderRender',
                        // width: 130
                    },
                    {
                        headerName: "Statut",
                        field: "Order_Status",
                        cellRendererFramework: 'badgeCellRenderer',
                        cellRendererParams: { componentName: "Orders", columnName: "Order_Status" },
                        // width: 200
                    },
                    {
                        headerName: "Heure de Sortie",
                        field: "Release_Time", // Assuming "Release_Hour" is the correct field name
                        valueGetter: function (params) {
                            if (params.data && params.data.Release_Time) {
                                return params.data.Release_Time.substring(0, 19).replace('T', ' ');
                            }
                            return '';
                        }
                    },
                    {
                        sortable: false,
                        resizable: false,
                        filter: false,
                        cellClass: 'centered-customeCellRender',
                        cellRendererFramework: 'buttonCellRenderer',
                        cellRendererParams: { onButtonClick: this.showDetails, icon: "read_more", tooltip: "voir les détails" },
                        headerName: '',
                        width: 100
                    },

                ],
                rows: []
            },
            fields: [{
                label: 'Code Article',
                key: 'No',
                sortable: true
            },
            {
                label: 'Désignation article',
                key: 'Description',
                sortable: true
            },
            {
                label: 'Prix',
                key: 'Unit_Price',
                sortable: true
            }, {
                label: 'Quantité',
                key: 'Quantity',
                sortable: true
            }
            ],
            commandesSelected: [],
            show: true,
        }
    },
    created() {
        this.$options.components.buttonCellRenderer = ButtonCellRenderer;
        this.$options.components.badgeCellRenderer = BadgeCellRenderer;
        this.loadData();
    },
    methods: {
        filterClicked(obj) {
            this.startDate = obj.sDate;
            this.endDate = obj.eDate;
            this.loadData();
        },
        async showDetails(event) {
            this.show = !this.show;
            let response = await this.$http.get("order_line/findWhere", {
                params: {
                    Document_No: "'" + event.No + "'"
                }
            }).catch(() => this.show = false);
            this.commandesSelected = response.data;
            this.show = !this.show;
            this.$refs['my-modal'].show();
        },
        async loadData() {
            this.show = true;
            let response = await this.$http.post("order_header/findWhere", this.generateDateFilters());
            this.agGrid.rows = [...response.data].reverse();
            this.agGrid.rows = this.agGrid.rows.filter(x => x.No.substring(0, 2) != 'RV')
            this.show = false;
        },
        generateDateFilters() {
            const filters = [
                {
                    "field": "Posting_Date",
                    "value": "",
                    "operator": "gt"
                },
                {
                    "field": "Posting_Date",
                    "value": "",
                    "operator": "lt"
                }
            ];
            // Convert start date string to Date and adjust by -1 day
            const startDateObj = new Date(this.startDate);
            startDateObj.setDate(startDateObj.getDate() - 1);
            // Convert end date string to Date and adjust by +1 day
            const endDateObj = new Date(this.endDate);
            endDateObj.setDate(endDateObj.getDate() + 1);
            // Format adjusted dates as strings in yyyy-MM-dd format
            const formattedStartDate = startDateObj.toISOString().split('T')[0];
            const formattedEndDate = endDateObj.toISOString().split('T')[0];
            // Set adjusted date values in the filter objects
            filters[0].value = formattedStartDate;
            filters[1].value = formattedEndDate;
            return filters;
        },
    }
}
</script>

<style lang="scss" scoped>
@media (max-width: 768px) {
    .card {
        background-color: initial;
        box-shadow: initial;
    }

    .card-body {
        padding: 0px;
    }
}
</style>